import React, { useMemo } from "react";
import { TablePaginationConfig } from "antd5/es/table";
import { FilterValue, SorterResult } from "antd5/es/table/interface";
import { isEqual, startCase } from "lodash";
import { Flex } from "styles/utility-components";
import { useLocation, useSearch } from "wouter";

import { EventNames, useTracking } from "lib/tracking";
import { parseDateValue } from "../../../lib/columnSort";
import { EllipsisTooltipWouterLink } from "../../../lib/core_components/EllipsisTooltip";
import { Table } from "../../../lib/core_components/Table";
import { ColumnType } from "../../../lib/core_components/Table/ColumnTypes";
import { tableRowSelection } from "../../../lib/core_components/Table/Table";
import { OpportunityFromSearch } from "../../../lib/hooks/api/opportunities/useOpportunities";
import { OpportunitySortBy, OpportunitySortOrder } from "../../../lib/types/graphQLEnums";
import { formatDateOrOpenEnded } from "../../../lib/utils";
import { toHumanRelativeDateFromNow } from "../../../lib/utils/relative_time";
import { Currency } from "../../app_layout/Typography";
import OpportunityResultsEmptyState from "../empty_states/OpportunityResultsEmptyState";
import { OpportunityStage } from "../opportunity_stage/OpportunityStage";
import { getOpportunitiesColumnCopy } from "../utils";
import {
  renderAssignedToCell,
  renderBuyerCell,
  renderDateCell,
  renderSupplierCell,
  TableCell,
} from "./OpportunityTableCells";

export type OpportunityColumns =
  | "name"
  | "buyers"
  | "stage"
  | "assignedTo"
  | "value"
  | "createdAt"
  | "updatedAt"
  | "closeDate"
  | "suppliers";

type SortState = {
  field: OpportunitySortBy;
  order: OpportunitySortOrder;
};

type OpportunitiesTableProps = {
  opportunities: OpportunityFromSearch[] | undefined;
  columnSettings: OpportunityColumns[];
  isLoading: boolean;
  isError: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  selectedRowKeys: string[];
  onClearFilters: () => void;
};

function getSortOrder(
  sort: SortState | undefined,
  key: OpportunitySortBy,
): "ascend" | "descend" | null {
  if (!sort || sort.field !== key) {
    return null;
  }
  return sort.order === OpportunitySortOrder.Asc ? "ascend" : "descend";
}

function getOpportunityColumns({
  sort,
  handleOpportunityRouteChange,
  columnSettings,
}: {
  sort: SortState | undefined;
  handleOpportunityRouteChange: (id: string) => void;
  columnSettings: OpportunityColumns[];
}): ColumnType<OpportunityFromSearch>[] {
  const name: ColumnType<OpportunityFromSearch> = {
    key: OpportunitySortBy.Name,
    title: getOpportunitiesColumnCopy("name"),
    render: (opportunity) => (
      <TableCell minWidth={390}>
        <EllipsisTooltipWouterLink
          fullText={opportunity.name ?? "--"}
          linkText={opportunity.name ?? "--"}
          linkProps={{
            to: `/${opportunity.id}`,
            onClick: (e) => {
              e.preventDefault();
              handleOpportunityRouteChange(opportunity.id);
            },
          }}
        />
      </TableCell>
    ),
    width: 390,
    ellipsis: true,
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.Name),
  };

  const buyers: ColumnType<OpportunityFromSearch> = {
    key: OpportunitySortBy.BuyerName,
    title: getOpportunitiesColumnCopy("buyerName"),
    render: (opportunity) => renderBuyerCell({ opportunity, minWidth: 200 }),
    ellipsis: true,
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.BuyerName),
  };

  const stage: ColumnType<OpportunityFromSearch> = {
    key: OpportunitySortBy.StageName,
    title: getOpportunitiesColumnCopy("stageName"),
    render: (opportunity) => (
      <TableCell minWidth={200}>
        <OpportunityStage
          label={opportunity.stage.name}
          color={opportunity.stage.color}
          iconName={opportunity.stage.iconName}
          iconColor={opportunity.stage.iconColor}
        />
      </TableCell>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.StageName),
  };

  const assignedTo: ColumnType<OpportunityFromSearch> = {
    key: "assignedTo",
    title: getOpportunitiesColumnCopy("assignedTo"),
    render: (opportunity) => renderAssignedToCell({ opportunity, minWidth: 164 }),
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.AssignedTo),
  };

  const value: ColumnType<OpportunityFromSearch> = {
    key: OpportunitySortBy.Value,
    title: getOpportunitiesColumnCopy("value"),
    render: (opportunity) => (
      <TableCell minWidth={200}>
        <Currency value={opportunity.value} longFormat />
      </TableCell>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.Value),
  };

  const createdAt: ColumnType<OpportunityFromSearch> = {
    key: OpportunitySortBy.CreatedAt,
    title: getOpportunitiesColumnCopy("createdAt"),
    render: (opportunity) => renderDateCell(opportunity.createdAt, 180),
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.CreatedAt),
  };

  const updatedAt: ColumnType<OpportunityFromSearch> = {
    key: OpportunitySortBy.UpdatedAt,
    title: getOpportunitiesColumnCopy("updatedAt"),
    render: (opportunity) => {
      const date = parseDateValue(opportunity.updatedAt);
      return (
        <TableCell minWidth={180}>
          {!opportunity.updatedAt || !date.isValid
            ? "-"
            : toHumanRelativeDateFromNow(date.toJSDate())}
        </TableCell>
      );
    },
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.UpdatedAt),
  };

  const closeDate: ColumnType<OpportunityFromSearch> = {
    key: OpportunitySortBy.CloseDate,
    title: getOpportunitiesColumnCopy("closeDate"),
    render: (opportunity) => formatDateOrOpenEnded(opportunity.closeDate),
    sorter: true,
    sortOrder: getSortOrder(sort, OpportunitySortBy.CloseDate),
  };

  const suppliers: ColumnType<OpportunityFromSearch> = {
    key: "suppliers",
    title: getOpportunitiesColumnCopy("suppliers"),
    render: (opportunity) => renderSupplierCell({ opportunity }),
    ellipsis: true,
  };

  const columns = {
    name,
    buyers,
    stage,
    assignedTo,
    value,
    createdAt,
    updatedAt,
    closeDate,
    suppliers,
  };

  return columnSettings.map((c) => columns[c]);
}

export function OpportunitiesTable({
  opportunities,
  columnSettings,
  isLoading,
  isError,
  onSelectionChange,
  sort,
  onSortChange,
  selectedRowKeys,
  onClearFilters,
}: OpportunitiesTableProps) {
  const [location, setLocation] = useLocation();
  const searchParams = useSearch();
  const { logEvent } = useTracking();

  const handleSelectionChange = (selectedKeys: string[]): void => {
    onSelectionChange?.(selectedKeys);
  };

  const columns = useMemo(() => {
    const handleOpportunityRouteChange = (id: string) => {
      setLocation(`/${id}`, { state: { from: location, previousSearchParams: searchParams } });
    };

    return getOpportunityColumns({
      sort,
      handleOpportunityRouteChange,
      columnSettings,
    });
  }, [sort, setLocation, location, searchParams, columnSettings]);

  const handleTableChange = (
    _: TablePaginationConfig,
    __: Record<string, FilterValue | null>,
    sorter: SorterResult<OpportunityFromSearch> | SorterResult<OpportunityFromSearch>[],
  ): void => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!sort && !currentSorter.order) {
      return;
    }

    const newSortState: SortState = {
      order:
        currentSorter.order === "descend" ? OpportunitySortOrder.Desc : OpportunitySortOrder.Asc,
      field: currentSorter.columnKey as OpportunitySortBy,
    };

    if (!isEqual(sort, newSortState)) {
      onSortChange?.(newSortState);
      logEvent(EventNames.tableSorted, {
        Context: "Column action",
        "Sorted by": startCase(newSortState.field),
        "Sort order": newSortState.order,
      });
    }
  };

  return (
    <Table
      ariaLabel="Opportunities table"
      dataSource={opportunities}
      loading={isLoading}
      columns={columns}
      isError={isError}
      scroll={{ x: "100%" }}
      rowKey="id"
      rowSelection={tableRowSelection(
        selectedRowKeys,
        handleSelectionChange,
        opportunities ?? [],
        "id",
      )}
      onChange={handleTableChange}
      locale={{
        emptyText: (
          <Flex justifyContent="center" alignItems="center">
            <OpportunityResultsEmptyState onClearFilters={onClearFilters} />
          </Flex>
        ),
      }}
    />
  );
}
